import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { theme } from '../../theme';
import AdminSetupWizard from './AdminSetupWizard';
import NewDeviceRequest from './NewDeviceRequest';
import { getDeviceFingerprint, getDeviceContext } from '../../utils/deviceFingerprint';

/**
 * Wraps the entire app. On mount it asks the server:
 *   1) Is admin setup complete?
 *   2) Is THIS device known and approved?
 *
 * Routes to:
 *   - <AdminSetupWizard/>   when setup-required
 *   - <NewDeviceRequest/>   when unknown / pending / awaiting-code / rejected
 *   - children              when approved (normal app)
 *
 * UX strategy — never make the user stare at a blank screen:
 *   - Cached approval (24 h) → render children immediately, revalidate in
 *     background. A change-of-status flips the UI a moment later instead of
 *     blocking initial paint.
 *   - 3.5 s wall-clock timeout on the server check → fail OPEN (treat as
 *     approved) so a sleeping Render free-tier server can't hide the app.
 *     The next page load tries again; if the device truly isn't approved,
 *     the protected API calls inside the app will 401 and route the user
 *     to the proper request screen.
 *   - Branded loading state with a "warming up server…" hint after 1.5 s so
 *     visitors know something's happening.
 */

const CACHE_KEY = 'oralzy_device_gate_v1';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;     // 24 h
const NETWORK_TIMEOUT_MS = 3500;

function readCache() {
    try {
        const raw = localStorage.getItem(CACHE_KEY);
        if (!raw) return null;
        const obj = JSON.parse(raw);
        if (!obj?.ts || Date.now() - obj.ts > CACHE_TTL_MS) return null;
        return obj;
    } catch { return null; }
}
function writeCache(payload) {
    try { localStorage.setItem(CACHE_KEY, JSON.stringify({ ...payload, ts: Date.now() })); } catch {}
}

export default function DeviceGate({ API_BASE, children }) {
    // Hydrate from cache immediately so the first paint isn't blank.
    const cached = readCache();
    const initialPhase = cached?.phase || 'loading';

    const [state, setState] = useState({
        phase: initialPhase,
        fingerprint: cached?.fingerprint || '',
        deviceCtx: getDeviceContext(),
        info: cached?.info || null,
    });
    const [showWarmingHint, setShowWarmingHint] = useState(false);
    const hintTimerRef = useRef(null);

    const refreshGate = async ({ background = false } = {}) => {
        // Surface "warming up…" after a short delay so a fast response doesn't flicker.
        if (!background) {
            hintTimerRef.current = setTimeout(() => setShowWarmingHint(true), 1500);
        }
        try {
            const fp = await getDeviceFingerprint();
            const r = await axios.post(
                `${API_BASE}/security/device-check`,
                { fingerprint: fp },
                { timeout: NETWORK_TIMEOUT_MS },
            );
            const phase = r.data.status;
            setState(s => ({ ...s, phase, fingerprint: fp, info: r.data }));
            // Only cache approved/setup phases. Unknown/pending should always re-check.
            if (phase === 'approved' || phase === 'setup-required') {
                writeCache({ phase, fingerprint: fp, info: r.data });
            }
        } catch (e) {
            // Backend unreachable / timeout / route missing → fail OPEN. The
            // app's normal auth layer (JWT 401 → forced logout) is the real
            // safety net for unauthorised usage; this gate is just a UX layer.
            console.warn('[DeviceGate] check failed (fail-open):', e.message);
            setState(s => ({ ...s, phase: 'approved', error: e.message }));
        } finally {
            if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
            setShowWarmingHint(false);
        }
    };

    useEffect(() => {
        // If cache says approved/setup, revalidate quietly in background — UI
        // already rendering. Otherwise foreground check with the loading shell.
        const isCached = !!cached;
        refreshGate({ background: isCached });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (state.phase === 'loading') {
        return (
            <div style={{
                minHeight: '100vh', display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                backgroundColor: theme.bg, padding: 20, gap: 18,
            }}>
                {/* Logo + spinner — branded, not the bare text we had before */}
                <div style={{ fontSize: '2.4rem', fontWeight: 800, color: theme.primary, letterSpacing: 1 }}>
                    ORALZY
                </div>
                <div style={{
                    width: 38, height: 38, border: '3px solid ' + (theme.border || '#e5e7eb'),
                    borderTopColor: theme.primary, borderRadius: '50%',
                    animation: 'oralzy-spin 0.8s linear infinite',
                }} />
                <div style={{ color: theme.textMuted, fontSize: '0.92rem', textAlign: 'center', maxWidth: 320 }}>
                    {showWarmingHint
                        ? 'Server warm ho raha hai… 5-10 sec lag sakte hain.'
                        : 'Loading…'}
                </div>
                <style>{`@keyframes oralzy-spin { to { transform: rotate(360deg); } }`}</style>
            </div>
        );
    }

    if (state.phase === 'setup-required') {
        return (
            <AdminSetupWizard
                API_BASE={API_BASE}
                fingerprint={state.fingerprint}
                deviceCtx={state.deviceCtx}
                onComplete={refreshGate}
            />
        );
    }

    if (state.phase === 'unknown' || state.phase === 'pending' || state.phase === 'awaiting-code' || state.phase === 'rejected' || state.phase === 'revoked') {
        return (
            <NewDeviceRequest
                API_BASE={API_BASE}
                fingerprint={state.fingerprint}
                deviceCtx={state.deviceCtx}
                initialStatus={state.phase}
                onApproved={refreshGate}
            />
        );
    }

    // approved → render the real app
    return children;
}
